import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { QuickSelectChips, AI_EDIT_CHIPS } from './QuickSelectChips';
import type { SelectedImageData } from './imageInsertTypes';
import { AI_IMAGE_PROMPT_MAX_LENGTH, AI_IMAGE_PROMPT_TOO_LONG_MESSAGE } from '../api/aiImageApi';
import { useAutoGrowTextarea } from '../hooks/useAutoGrowTextarea';

export interface AIEditState {
  prompt: string;
  editedImageUrl: string | null;
  editedMimeType: string | null;
  isEditing: boolean;
  error: string | null;
}

export function createInitialAIEditState(): AIEditState {
  return {
    prompt: '',
    editedImageUrl: null,
    editedMimeType: null,
    isEditing: false,
    error: null,
  };
}

interface AIEditPanelProps {
  sourceImageUrl: string;
  sourceImageAlt: string;
  state: AIEditState;
  onStateChange: (state: AIEditState) => void;
  onEdit: (prompt: string) => Promise<{ imageUrl: string; mimeType: string }>;
  onImageReady: (data: SelectedImageData) => void;
  onBack: () => void;
  targetAspectRatio?: string;
}

export function AIEditPanel({
  sourceImageUrl,
  sourceImageAlt,
  state,
  onStateChange,
  onEdit,
  onImageReady,
  onBack,
  targetAspectRatio,
}: AIEditPanelProps) {
  const { tokens } = useTheme();
  const styles = useMemo(() => createStyles(tokens), [tokens]);
  const [localPrompt, setLocalPrompt] = useState(state.prompt);
  const textareaRef = useAutoGrowTextarea(localPrompt);
  const isPromptValid =
    localPrompt.trim().length > 0 && localPrompt.length <= AI_IMAGE_PROMPT_MAX_LENGTH;
  const charCount = localPrompt.length;
  const isOverLimit = charCount > AI_IMAGE_PROMPT_MAX_LENGTH;

  const updateState = useCallback(
    (updates: Partial<AIEditState>) => {
      onStateChange({ ...state, ...updates });
    },
    [state, onStateChange]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!isPromptValid || state.isEditing) return;

      updateState({ isEditing: true, error: null, prompt: localPrompt.trim() });

      try {
        const result = await onEdit(localPrompt.trim());
        updateState({
          editedImageUrl: result.imageUrl,
          editedMimeType: result.mimeType,
          isEditing: false,
        });
      } catch (error) {
        updateState({
          error: error instanceof Error ? error.message : 'Failed to edit image',
          isEditing: false,
        });
      }
    },
    [isPromptValid, localPrompt, state.isEditing, updateState, onEdit]
  );

  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalPrompt(e.target.value);
  }, []);

  const handleChipSelect = useCallback((value: string) => {
    setLocalPrompt((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}, ${value}` : value;
    });
  }, []);

  const handleUseEdited = useCallback(async () => {
    if (!state.editedImageUrl) return;

    const response = await fetch(state.editedImageUrl);
    const blob = await response.blob();

    onImageReady({
      previewUrl: state.editedImageUrl,
      blob,
      needsUpload: true,
      suggestedAlt: '',
      aiGeneratedAlt: false,
      mimeType: state.editedMimeType || 'image/png',
      sourceType: 'ai-edit',
    });
  }, [state.editedImageUrl, state.editedMimeType, localPrompt, onImageReady]);

  const handleTryAgain = useCallback(() => {
    updateState({ editedImageUrl: null, editedMimeType: null, error: null });
  }, [updateState]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (isPromptValid && !state.isEditing) {
          const form = e.currentTarget.closest('form');
          if (form) {
            form.requestSubmit();
          }
        }
      }
    },
    [isPromptValid, state.isEditing]
  );

  // Show comparison view when edit is complete
  if (state.editedImageUrl && !state.isEditing) {
    return (
      <div style={styles.container}>
        <button
          type="button"
          style={styles.backButton}
          onClick={onBack}
          aria-label="Back to manage image"
        >
          Back
        </button>

        <div style={styles.comparisonContainer}>
          <div style={styles.comparisonItem}>
            <p style={styles.comparisonLabel}>Current Image</p>
            <div style={styles.comparisonImageContainer}>
              <img src={sourceImageUrl} alt={sourceImageAlt} style={styles.comparisonImage} />
            </div>
          </div>
          <div style={styles.comparisonItem}>
            <p style={styles.comparisonLabelNew}>New</p>
            <div style={styles.comparisonImageContainer}>
              <img
                src={state.editedImageUrl}
                alt={`Edited: ${state.prompt}`}
                style={styles.comparisonImage}
              />
            </div>
          </div>
        </div>

        <div style={styles.resultActions}>
          <button
            type="button"
            style={styles.tryAgainButton}
            onClick={handleTryAgain}
            aria-label="Try editing again"
          >
            Try Again
          </button>
          <button
            type="button"
            style={styles.useButton}
            onClick={handleUseEdited}
            aria-label="Use edited image"
          >
            Use Edited Image
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button
        type="button"
        style={{
          ...styles.backButton,
          ...(state.isEditing ? styles.buttonDisabled : {}),
        }}
        onClick={onBack}
        disabled={state.isEditing}
        aria-label="Back to manage image"
      >
        Back
      </button>

      <div style={styles.sourceImageSection}>
        <p style={styles.sectionLabel}>Current Image</p>
        <div style={styles.sourceImageContainer}>
          <img src={sourceImageUrl} alt={sourceImageAlt} style={styles.sourceImage} />
        </div>
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <label style={styles.label}>
          <span style={styles.labelText}>Describe how you want to edit this image</span>
          <textarea
            ref={textareaRef}
            value={localPrompt}
            onChange={handlePromptChange}
            onKeyDown={handleKeyDown}
            placeholder="Be specific about what to change"
            style={{
              ...styles.textarea,
              ...(isOverLimit ? styles.textareaError : {}),
            }}
            rows={8}
            disabled={state.isEditing}
            aria-describedby="edit-char-count"
            aria-invalid={isOverLimit}
          />
          <div style={styles.textareaFooter}>
            <span style={styles.validationMessage} role="alert">
              {isOverLimit ? AI_IMAGE_PROMPT_TOO_LONG_MESSAGE : ''}
            </span>
            <span
              id="edit-char-count"
              style={{
                ...styles.charCount,
                ...(isOverLimit ? styles.charCountError : {}),
              }}
              aria-live="polite"
            >
              {charCount}/{AI_IMAGE_PROMPT_MAX_LENGTH}
            </span>
          </div>
        </label>

        <QuickSelectChips
          chips={AI_EDIT_CHIPS}
          onSelect={handleChipSelect}
          disabled={state.isEditing}
        />

        {!state.isEditing && (
          <button
            type="submit"
            style={{
              ...styles.editButton,
              ...(!isPromptValid ? styles.editButtonDisabled : {}),
            }}
            disabled={!isPromptValid}
            aria-label="Apply edit to image"
          >
            Apply Edit
          </button>
        )}
      </form>

      {state.error && (
        <div style={styles.errorMessage} role="alert" aria-live="assertive">
          {state.error}
        </div>
      )}

      {state.isEditing && (
        <div style={styles.loadingContainer} aria-live="polite" role="status">
          <div style={styles.loadingSpinner} aria-hidden="true" />
          <p style={styles.loadingText}>Editing your image...</p>
          <p style={styles.loadingHint}>This may take a minute</p>
        </div>
      )}
    </div>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, React.CSSProperties> {
  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      flex: 1,
      overflow: 'auto',
    },
    backButton: {
      padding: '8px 12px',
      fontSize: '14px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      minWidth: '44px',
      minHeight: '44px',
      alignSelf: 'flex-start',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    aspectRatioHint: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    sourceImageSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    sectionLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
      margin: 0,
    },
    sourceImageContainer: {
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surface,
    },
    sourceImage: {
      width: '100%',
      maxHeight: '280px',
      objectFit: 'contain',
      display: 'block',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    labelText: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
    },
    textarea: {
      padding: '12px',
      fontSize: '14px',
      lineHeight: 1.5,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: tokens.text.subtle,
      borderRadius: '6px',
      outline: 'none',
      fontFamily: 'inherit',
      resize: 'vertical',
      boxSizing: 'border-box',
      // Auto-grows with content (useAutoGrowTextarea) between an 8-line floor
      // (8 × 21px line + 24px padding + 2px border) and a viewport-relative cap.
      minHeight: '194px',
      maxHeight: '40vh',
      overflowY: 'auto',
      backgroundColor: tokens.backgrounds.app,
      color: tokens.text.primary,
    },
    textareaError: {
      borderColor: tokens.ui.errorBorder,
    },
    validationMessage: {
      fontSize: '12px',
      color: tokens.ui.errorText,
    },
    textareaFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    hint: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    charCount: {
      fontSize: '12px',
      color: tokens.text.subtle,
    },
    charCountError: {
      color: tokens.ui.errorText,
    },
    editButton: {
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    editButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: `2px solid color-mix(in srgb, ${tokens.text.onBrand} 30%, transparent)`,
      borderTopColor: tokens.text.onBrand,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },
    errorMessage: {
      padding: '12px',
      backgroundColor: tokens.ui.errorBackground,
      border: `1px solid ${tokens.ui.errorBorder}`,
      borderRadius: '6px',
      color: tokens.ui.errorText,
      fontSize: '14px',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
    },
    loadingSpinner: {
      width: '40px',
      height: '40px',
      border: `4px solid ${tokens.text.subtle}`,
      borderTopColor: tokens.ui.selectionBorder,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '12px',
    },
    loadingText: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.text.primary,
      margin: 0,
    },
    loadingHint: {
      fontSize: '13px',
      color: tokens.text.subtle,
      marginTop: '6px',
    },
    comparisonContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    comparisonItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    comparisonLabel: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.text.muted,
      margin: 0,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    comparisonLabelNew: {
      fontSize: '13px',
      fontWeight: 600,
      color: tokens.ui.selectionBorder,
      margin: 0,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    comparisonImageContainer: {
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surface,
    },
    comparisonImage: {
      width: '100%',
      maxHeight: '180px',
      objectFit: 'contain',
      display: 'block',
    },
    promptUsed: {
      fontSize: '13px',
      color: tokens.text.muted,
      margin: 0,
      lineHeight: 1.5,
    },
    resultActions: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
    },
    tryAgainButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      minWidth: '44px',
      minHeight: '44px',
    },
    useButton: {
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      borderRadius: '6px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      cursor: 'pointer',
      minWidth: '44px',
      minHeight: '44px',
    },
  };
}
